import React, { useState, useEffect, Suspense } from "react";
import {
  getAllMerchandiseSalesAPI,
  getMerchandiseSalesAnalyticsAPI,
  getMerchandiseSalesSummaryAPI,
  exportMerchandiseSalesAPI,
} from "../../API/MerchandiseSalesAPI";
import ButtonLoader from "../../components/Loader/Loader";
import SimplePagination from "../../components/Pagination/SimplePagination";
import toast from "react-hot-toast";
import {
  FiDollarSign,
  FiShoppingBag,
  FiTrendingUp,
  FiDownload,
  FiFilter,
  FiX,
  FiCalendar,
  FiPackage,
  FiUser,
} from "react-icons/fi";

const Wrapper = React.lazy(() => import("../../components/wrapper/Wrapper"));

function MerchandiseSales() {
  // State management
  const [loading, setLoading] = useState(false);
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [paginationInfo, setPaginationInfo] = useState({});
  const [filterOpen, setFilterOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    userId: "",
    productId: "",
  });

  // Fetch summary
  const fetchSummary = React.useCallback(async () => {
    try {
      const response = await getMerchandiseSalesSummaryAPI();
      setSummary(response.data);
    } catch (error) {
      console.error("Error fetching summary:", error);
    }
  }, []);

  // Fetch analytics
  const fetchAnalytics = React.useCallback(async () => {
    try {
      const response = await getMerchandiseSalesAnalyticsAPI(filters);
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    }
  }, [filters]);

  // Fetch sales
  const fetchSales = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllMerchandiseSalesAPI({
        page: pageNumber,
        limit: pageLimit,
        ...filters,
      });
      setSales(response.data.sales || []);
      setPaginationInfo(response.data.paginationInfo || {});
    } catch (error) {
      toast.error("Failed to load sales data");
      console.error("Error fetching sales:", error);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageLimit, filters]);

  // Initial load
  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Load sales and analytics when filters or pagination change
  useEffect(() => {
    fetchSales();
    fetchAnalytics();
  }, [fetchSales, fetchAnalytics]);

  // Apply filters
  const handleApplyFilters = () => {
    setPageNumber(1);
    setFilterOpen(false);
    fetchSales();
    fetchAnalytics();
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      startDate: "",
      endDate: "",
      userId: "",
      productId: "",
    });
    setPageNumber(1);
    setFilterOpen(false);
  };

  // Export data
  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await exportMerchandiseSalesAPI(filters);

      // Convert to CSV
      const csvData = convertToCSV(response.data.exportData);

      // Download
      const blob = new Blob([csvData], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `merchandise-sales-${new Date().toISOString().split("T")[0]
        }.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success("Sales data exported successfully");
    } catch (error) {
      toast.error("Error exporting data");
    } finally {
      setExporting(false);
    }
  };

  // Convert to CSV helper
  const convertToCSV = (data) => {
    if (!data || data.length === 0) return "";

    const headers = Object.keys(data[0]).join(",");
    const rows = data.map((row) =>
      Object.values(row)
        .map((value) => `"${value}"`)
        .join(",")
    );

    return [headers, ...rows].join("\n");
  };

  const handleLimitChange = (newLimit) => {
    setPageLimit(newLimit);
    setPageNumber(1);
  };

  return (
    <Suspense
      fallback={
        <div className="w-full pt-40 flex justify-center items-center">
          <ButtonLoader />
        </div>
      }
    >
      <Wrapper>
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <FiShoppingBag className="w-7 h-7 text-[--primary]" />
              Merchandise Sales
            </h1>
            <p className="text-gray-600 mt-1">
              Track and analyze merchandise sales performance
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setFilterOpen(!filterOpen)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              <FiFilter className="w-4 h-4" />
              Filters
            </button>
            <button
              onClick={handleExport}
              disabled={exporting || sales.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[--primary] text-white rounded-lg hover:bg-[--primary]/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Exporting...
                </>
              ) : (
                <>
                  <FiDownload className="w-4 h-4" />
                  Export CSV
                </>
              )}
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        {filterOpen && (
          <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
              <button
                onClick={() => setFilterOpen(false)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    setFilters({ ...filters, startDate: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) =>
                    setFilters({ ...filters, endDate: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User ID (Optional)
                </label>
                <input
                  type="text"
                  value={filters.userId}
                  onChange={(e) =>
                    setFilters({ ...filters, userId: e.target.value })
                  }
                  placeholder="Filter by user"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product ID (Optional)
                </label>
                <input
                  type="text"
                  value={filters.productId}
                  onChange={(e) =>
                    setFilters({ ...filters, productId: e.target.value })
                  }
                  placeholder="Filter by product"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Clear
              </button>
              <button
                onClick={handleApplyFilters}
                className="px-4 py-2 bg-[--primary] text-white rounded-lg hover:bg-[--primary]/90 transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Today's Revenue</p>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    ${summary.today.revenue.toFixed(2)}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {summary.today.salesCount} sales
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <FiDollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">This Month</p>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    ${summary.thisMonth.revenue.toFixed(2)}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {summary.thisMonth.salesCount} sales
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FiCalendar className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">This Year</p>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    ${summary.thisYear.revenue.toFixed(2)}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {summary.thisYear.salesCount} sales
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FiTrendingUp className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">All Time</p>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    ${summary.allTime.revenue.toFixed(2)}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {summary.allTime.salesCount} sales
                  </p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <FiShoppingBag className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Section */}
        {analytics && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Top Products */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FiPackage className="w-5 h-5 text-[--primary]" />
                Top Selling Products
              </h3>
              <div className="space-y-3">
                {analytics.topProducts && analytics.topProducts.length > 0 ? (
                  analytics.topProducts.map((product, idx) => (
                    <div
                      key={product.productId}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[--primary] text-white rounded-full flex items-center justify-center font-bold text-sm">
                          {idx + 1}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {product.productTitle}
                          </p>
                          <p className="text-xs text-gray-500">
                            {product.totalQuantity} units sold
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-green-600">
                          ${product.totalRevenue.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {product.salesCount} sales
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No sales data available
                  </p>
                )}
              </div>
            </div>

            {/* Top Athletes */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FiUser className="w-5 h-5 text-[--primary]" />
                Top Athletes
              </h3>
              <div className="space-y-3">
                {analytics.topAthletes && analytics.topAthletes.length > 0 ? (
                  analytics.topAthletes.map((athlete, idx) => (
                    <div
                      key={athlete.userId}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[--primary] text-white rounded-full flex items-center justify-center font-bold text-sm">
                          {idx + 1}
                        </div>
                        <div className="flex items-center gap-2">
                          {athlete.profilePhoto ? (
                            <img
                              src={athlete.profilePhoto}
                              alt={athlete.userName}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                              <FiUser className="w-4 h-4 text-gray-600" />
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {athlete.userName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {athlete.totalQuantity} units
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-green-600">
                          ${athlete.totalRevenue.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {athlete.salesCount} sales
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No athlete data available
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Overview Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-sm p-6 text-white">
              <p className="text-green-100 text-sm">Total Revenue</p>
              <h3 className="text-3xl font-bold mt-1">
                ${analytics.overview.totalRevenue.toFixed(2)}
              </h3>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-sm p-6 text-white">
              <p className="text-blue-100 text-sm">Units Sold</p>
              <h3 className="text-3xl font-bold mt-1">
                {analytics.overview.totalQuantity}
              </h3>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-sm p-6 text-white">
              <p className="text-purple-100 text-sm">Total Sales</p>
              <h3 className="text-3xl font-bold mt-1">
                {analytics.overview.totalSales}
              </h3>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-sm p-6 text-white">
              <p className="text-orange-100 text-sm">Avg Sale Value</p>
              <h3 className="text-3xl font-bold mt-1">
                ${analytics.overview.avgSaleValue.toFixed(2)}
              </h3>
            </div>
          </div>
        )}

        {/* Sales Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Recent Sales
            </h3>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <ButtonLoader />
            </div>
          ) : sales.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Athlete
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Price
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Variants
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sales.map((sale) => (
                    <tr
                      key={sale._id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {new Date(sale.saleDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {sale.product?.productImage?.[0] ? (
                            <img
                              src={sale.product.productImage[0]}
                              alt={sale.product.title}
                              className="w-10 h-10 object-cover rounded-lg border border-gray-200"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                              <FiPackage className="w-5 h-5 text-gray-400" />
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {sale.product?.title || "N/A"}
                            </p>
                            <p className="text-xs text-gray-500">
                              ${sale.request?.price || 0} per unit
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {sale.user?.profilePhoto ? (
                            <img
                              src={sale.user.profilePhoto}
                              alt={sale.user.firstName}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                              <FiUser className="w-4 h-4 text-gray-600" />
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {sale.user?.firstName} {sale.user?.lastName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {sale.user?.phoneNumber}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-semibold text-gray-900">
                          {sale.quantity}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-bold text-green-600">
                          ${sale.totalPrice.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {sale.request?.color?.length > 0 && (
                            <div className="flex items-center gap-1">
                              {sale.request.color
                                .slice(0, 3)
                                .map((color, idx) => (
                                  <div
                                    key={idx}
                                    className="w-4 h-4 rounded-full border border-gray-300"
                                    style={{ backgroundColor: color }}
                                    title={color}
                                  ></div>
                                ))}
                              {sale.request.color.length > 3 && (
                                <span className="text-xs text-gray-500">
                                  +{sale.request.color.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                          {sale.request?.size?.length > 0 && (
                            <div className="flex gap-1 flex-wrap">
                              {sale.request.size
                                .slice(0, 3)
                                .map((size, idx) => (
                                  <span
                                    key={idx}
                                    className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded"
                                  >
                                    {size}
                                  </span>
                                ))}
                              {sale.request.size.length > 3 && (
                                <span className="text-xs text-gray-500">
                                  +{sale.request.size.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <FiShoppingBag className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No sales found
              </h3>
              <p className="text-gray-500">
                No merchandise sales match your current filters.
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && sales.length > 0 && (
          <SimplePagination
            totalPages={paginationInfo?.totalPages || 0}
            currentPage={paginationInfo?.currentPage || 1}
            onPageChange={setPageNumber}
            limit={pageLimit}
            onLimitChange={handleLimitChange}
            totalItems={paginationInfo?.totalItems || 0}
          />
        )}
      </Wrapper>
    </Suspense>
  );
}

export default MerchandiseSales;
